import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { fetchPromotedSearchResults } from '@/redux/modules/search/search.api';
import { SearchResultPayload } from '@/redux/modules/search/search.types';
import { AppDispatch, AppGetState } from '@/redux/store';
import { TypedActionWithPayload } from '@/types/redux';
import { AugmentedSearchQuery, SearchPageName, SearchQuery } from '@/types/search/searchQuery';

import { getIsBot } from './analytics';
import { getClientIpAddress } from './browser';
import { getDeployment } from './config';
import { getUserExcludedFacets } from './search/exclusions/searchExclusions.selectors';
import { getAugmentedSearchOptions } from './search/search.helpers';

export const CLEAR_PROMOTED_SEARCH_RESULTS = 'CLEAR_PROMOTED_SEARCH_RESULTS';
export const LOAD_PROMOTED_SEARCH_RESULTS_FAIL = 'LOAD_PROMOTED_SEARCH_RESULTS_FAIL';
export const LOAD_PROMOTED_SEARCH_RESULTS_REQUEST = 'LOAD_PROMOTED_SEARCH_RESULTS_REQUEST';
export const LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS = 'LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS';
export type LoadPromotedSearchResultsSuccessAction = TypedActionWithPayload<
    typeof LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS,
    SearchResultPayload
>;

// reducer
export type SearchPromotedSlice = {
    all: number[];
    breadcrumbs: { id: string; name: string; url: string }[];
    itemIds: number[];
};

export const defaultSearchPromotedSlice: SearchPromotedSlice = {
    all: [],
    breadcrumbs: [],
    itemIds: [],
};

export default function reducer(state = defaultSearchPromotedSlice, action: any = {}): SearchPromotedSlice {
    switch (action.type) {
        case CLEAR_PROMOTED_SEARCH_RESULTS:
        case LOAD_PROMOTED_SEARCH_RESULTS_FAIL:
            return defaultSearchPromotedSlice;
        case LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS:
            const itemIDs = (action.payload.itemIDs || []) as number[];
            const breadcrumbs = action.payload.breadcrumbs || [];

            return {
                ...state,
                all: [...new Set([...state.all, ...itemIDs])], // prevent duplicates
                breadcrumbs,
                itemIds: itemIDs,
            };
        case LOAD_PROMOTED_SEARCH_RESULTS_REQUEST:
        default:
            return state;
    }
}

/* ACTION CREATORS */
export const clearPromotedSearchResults = () => ({
    payload: { itemIDs: [] },
    type: CLEAR_PROMOTED_SEARCH_RESULTS,
});

type SearchPromotedPayload = {
    ids?: number[];
    pageName: string;
    rankingVersionVariant?: string;
    searchQuery: SearchQuery;
    useScoutCategories?: boolean;
};

export const searchPromoted =
    ({ ids, pageName, rankingVersionVariant, searchQuery, useScoutCategories }: SearchPromotedPayload) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        try {
            const state = getState();
            const deployment = getDeployment(state);
            const authToken = getAuthToken(state);
            const ipAddress = getClientIpAddress(state);
            const isBot = getIsBot(state);

            if (
                pageName === SearchPageName.CATALOG ||
                pageName === SearchPageName.PRICE_GUIDE ||
                pageName === SearchPageName.SOLD_SEARCH
            ) {
                return dispatch(clearPromotedSearchResults());
            }
            dispatch({
                payload: searchQuery,
                type: LOAD_PROMOTED_SEARCH_RESULTS_REQUEST,
            });

            /**
             * Annotate facets which have user-defined search exclusions, and fold in any
             * page-level house exclusions. Shares `getAugmentedSearchOptions` with the organic
             * search path -- this used to be a copy of it, which meant a sponsored lot from an
             * excluded house could still surface on a page that excluded that house.
             *
             * Deliberately shares only the options builder, not `getAugmentedSearchQuery`: that
             * would also add facetOverrides and topRated to the promoted payload, which is
             * unrelated to the exclusions and changes what search-party is asked for.
             */
            const searchExclusions = getUserExcludedFacets(state);
            // Frontend-only field, folded into `options` below; must never reach the wire.
            const { excludedAuctionHouseIds, ...queryForRequest } = searchQuery;
            const augmentedSearchQuery: AugmentedSearchQuery = {
                ...(isBot ? { isBot } : {}),
                ...queryForRequest,
                options: getAugmentedSearchOptions(searchQuery.options, searchExclusions, excludedAuctionHouseIds),
                /*
                 * Only when there is a term to key the metadata on -- an empty one makes
                 * search-party 404 the whole request -- and only when buildSearchQuery has not
                 * already opted this page out (LAAC-2380: an unregistered non-empty term 404s too).
                 * Same rule, and same reason, as the organic path in search.actions; see there.
                 */
                seoSearch:
                    searchQuery.seoSearch ??
                    ((pageName === SearchPageName.SEO_SEARCH || pageName === SearchPageName.COLLECTIONS) &&
                        Boolean(searchQuery.searchTerm)),
            };

            const results = await fetchPromotedSearchResults({
                authToken,
                deployment,
                ids,
                ipAddress,
                rankingVersionVariant,
                searchQuery: augmentedSearchQuery,
                useScoutCategories,
            });
            if (results.error) {
                dispatch({
                    error: true,
                    meta: { augmentedSearchQuery },
                    payload: results.payload,
                    type: LOAD_PROMOTED_SEARCH_RESULTS_FAIL,
                });
                return dispatch(clearPromotedSearchResults());
            } else {
                return dispatch({
                    meta: { actionTime: Date.now() },
                    payload: results.payload,
                    type: LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS,
                });
            }
        } catch (error) {
            dispatch({
                error: true,
                meta: { searchQuery },
                payload: error,
                type: LOAD_PROMOTED_SEARCH_RESULTS_FAIL,
            });
            return dispatch(clearPromotedSearchResults());
        }
    };
